import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, Upload, FileText, X, Copy, ArrowRight, User, Car, IdCard, ShieldCheck, PenLine, ListChecks } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import { API } from "@/lib/api";
import { T } from "@/lib/testIds";

const STEPS = [
  { key: "personal", label: "Personal", Icon: User },
  { key: "driver", label: "Driver", Icon: IdCard },
  { key: "vehicle", label: "Vehicle", Icon: Car },
  { key: "documents", label: "Documents", Icon: Upload },
  { key: "agreements", label: "Agreements", Icon: ShieldCheck },
  { key: "signature", label: "Signature", Icon: PenLine },
  { key: "review", label: "Review", Icon: ListChecks },
];

const DOC_LIST = [
  { key: "driver_license", label: "Driver License", required: true },
  { key: "mvr", label: "Motor Vehicle Record (MVR)", required: false },
  { key: "additional", label: "Additional Documents", required: false },
];

const ELIGIBILITY = [
  "Valid driver's license",
  "Acceptable driving history",
  "Required driving experience",
  "Vehicle requirements met",
  "Drug test screening",
  "Background check authorization",
];

const initial = {
  first_name: "", last_name: "", phone: "", email: "", city: "", state: "", zip: "", dob: "",
  license_number: "", license_state: "", license_expiration: "", years_experience: "", driving_history: "",
  vehicle_year: "", vehicle_make: "", vehicle_model: "",
  documents: {},
  waiver_accepted: false,
  authorizations_accepted: false,
  signature_data: "",
};

const REQUIRED_BY_STEP = {
  personal: ["first_name", "last_name", "phone", "email", "city", "state", "zip", "dob"],
  driver: ["license_number", "license_state", "license_expiration", "years_experience"],
  vehicle: ["vehicle_year", "vehicle_model"],
};

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label} {required && <span className="text-red-500">*</span>}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Input({ testKey, ...props }) {
  return <input data-testid={T.apply.field(testKey)} {...props} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none" />;
}

export default function DriverApplication() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const canNext = () => {
    const stepKey = STEPS[step].key;
    if (stepKey === "personal" || stepKey === "driver" || stepKey === "vehicle") {
      const missing = REQUIRED_BY_STEP[stepKey].filter((k) => !String(data[k] || "").trim());
      if (missing.length) { toast.error("Please complete all required fields."); return false; }
      if (stepKey === "personal" && !/^\S+@\S+\.\S+$/.test(data.email)) { toast.error("Enter a valid email."); return false; }
    }
    if (stepKey === "documents") {
      const missing = DOC_LIST.filter((d) => d.required && !data.documents[d.key]);
      if (missing.length) { toast.error(`Please upload: ${missing.map((m) => m.label).join(", ")}`); return false; }
    }
    if (stepKey === "agreements") {
      if (!data.waiver_accepted) { toast.error("Please accept the Release, Waiver of Liability."); return false; }
    }
    if (stepKey === "signature") {
      if (!data.signature_data) { toast.error("Please provide your digital signature."); return false; }
    }
    return true;
  };

  const next = () => { if (canNext()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const uploadDoc = async (docKey, file) => {
    if (!file) return;
    if (!["application/pdf", "image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      toast.error("Only PDF, JPG, PNG accepted");
      return;
    }
    try {
      setUploading((u) => ({ ...u, [docKey]: true }));
      const form = new FormData();
      form.append("file", file);
      form.append("label", docKey);
      const { data: resp } = await axios.post(`${API}/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setData((d) => ({ ...d, documents: { ...d.documents, [docKey]: resp } }));
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [docKey]: false }));
    }
  };

  const removeDoc = (docKey) => setData((d) => ({ ...d, documents: { ...d.documents, [docKey]: undefined } }));

  const submit = async () => {
    try {
      setSubmitting(true);
      const documents = Object.entries(data.documents).filter(([, v]) => !!v).map(([k, v]) => ({ ...v, label: k }));
      const payload = { ...data, documents, signature_date: new Date().toISOString() };
      delete payload.authorizations_accepted;
      const { data: resp } = await axios.post(`${API}/applications`, payload);
      setResult(resp);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 animate-fade-up">
        <div className="rounded-3xl bg-white border border-slate-200 p-10 text-center shadow-lg">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><CheckCircle2 className="w-8 h-8" /></div>
          <h1 className="mt-5 text-3xl font-bold text-[#0A192F]">Application Submitted Successfully</h1>
          <p className="mt-3 text-slate-600">Your application has been received and will be reviewed by our team.</p>
          <div className="mt-6 inline-flex items-center gap-3 bg-slate-50 rounded-2xl px-5 py-3 border border-slate-200">
            <span className="text-sm text-slate-500">Application ID</span>
            <span data-testid={T.apply.successId} className="font-mono font-bold text-[#0A192F]">{result.application_id}</span>
            <button onClick={() => { navigator.clipboard.writeText(result.application_id); toast.success("Copied"); }} className="p-1.5 rounded-md hover:bg-slate-200" aria-label="Copy application ID">
              <Copy className="w-4 h-4 text-slate-600" />
            </button>
          </div>
          <p className="mt-5 text-xs text-slate-500">Save this ID — you'll use it (along with your last name) to check your application status. A confirmation email has been sent to your inbox.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate("/status")} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#1D4ED8] text-white font-semibold hover:bg-blue-800">Check Status <ArrowRight className="w-4 h-4" /></button>
            <button onClick={() => navigate("/")} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-slate-200 text-slate-800 font-semibold hover:bg-slate-50">Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  const stepKey = STEPS[step].key;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fade-up">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#1D4ED8] font-semibold mb-2">Driver Application</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0A192F]">Join Moreno Quality Transportation</h1>
        <p className="mt-2 text-slate-600">Complete each section to submit your application.</p>
      </div>

      {/* Progress */}
      <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-6 overflow-x-auto">
        <ol className="flex items-center gap-2 min-w-max">
          {STEPS.map((s, i) => {
            const active = i === step;
            const done = i < step;
            return (
              <li key={s.key} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => i <= step && setStep(i)}
                  data-testid={T.apply.section(s.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold transition-colors ${
                    active ? "bg-[#1D4ED8] text-white" : done ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                  }`}
                >
                  <s.Icon className="w-3.5 h-3.5" /> {s.label}
                </button>
                {i < STEPS.length - 1 && <span className="w-4 h-px bg-slate-200" />}
              </li>
            );
          })}
        </ol>
      </div>

      <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm">
        {/* Personal */}
        {stepKey === "personal" && (
          <div className="grid md:grid-cols-2 gap-5">
            <h2 className="md:col-span-2 text-xl font-semibold text-[#0A192F]">Personal Information</h2>
            <Field label="First Name" required><Input testKey="first_name" value={data.first_name} onChange={(e) => setField("first_name", e.target.value)} /></Field>
            <Field label="Last Name" required><Input testKey="last_name" value={data.last_name} onChange={(e) => setField("last_name", e.target.value)} /></Field>
            <Field label="Phone Number" required><Input testKey="phone" type="tel" value={data.phone} onChange={(e) => setField("phone", e.target.value)} /></Field>
            <Field label="Email Address" required><Input testKey="email" type="email" value={data.email} onChange={(e) => setField("email", e.target.value)} /></Field>
            <Field label="City" required><Input testKey="city" value={data.city} onChange={(e) => setField("city", e.target.value)} /></Field>
            <Field label="State" required><Input testKey="state" value={data.state} onChange={(e) => setField("state", e.target.value)} /></Field>
            <Field label="ZIP Code" required><Input testKey="zip" value={data.zip} onChange={(e) => setField("zip", e.target.value)} /></Field>
            <Field label="Date of Birth" required><Input testKey="dob" type="date" value={data.dob} onChange={(e) => setField("dob", e.target.value)} /></Field>
          </div>
        )}

        {/* Driver */}
        {stepKey === "driver" && (
          <div className="grid md:grid-cols-2 gap-5">
            <h2 className="md:col-span-2 text-xl font-semibold text-[#0A192F]">Driver Information</h2>
            <Field label="Driver License Number" required><Input testKey="license_number" value={data.license_number} onChange={(e) => setField("license_number", e.target.value)} /></Field>
            <Field label="State of Driver License" required><Input testKey="license_state" value={data.license_state} onChange={(e) => setField("license_state", e.target.value)} /></Field>
            <Field label="Driver License Expiration Date" required><Input testKey="license_expiration" type="date" value={data.license_expiration} onChange={(e) => setField("license_expiration", e.target.value)} /></Field>
            <Field label="Years of Driving Experience" required><Input testKey="years_experience" type="number" min="0" value={data.years_experience} onChange={(e) => setField("years_experience", e.target.value)} /></Field>
            <div className="md:col-span-2">
              <Field label="Prior experience as a driver (optional)">
                <textarea data-testid={T.apply.field("driving_history")} rows={4} value={data.driving_history} onChange={(e) => setField("driving_history", e.target.value)} placeholder="Have you driven professionally before? Rideshare, delivery, NEMT, taxi, chauffeur, etc." className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none" />
              </Field>
            </div>
          </div>
        )}

        {/* Vehicle */}
        {stepKey === "vehicle" && (
          <div className="grid md:grid-cols-2 gap-5">
            <h2 className="md:col-span-2 text-xl font-semibold text-[#0A192F]">Vehicle Information</h2>
            <p className="md:col-span-2 text-sm text-slate-500 -mt-2">Vehicle details are used for application review.</p>
            <Field label="Vehicle Year" required><Input testKey="vehicle_year" value={data.vehicle_year} onChange={(e) => setField("vehicle_year", e.target.value)} /></Field>
            <Field label="Vehicle Model" required><Input testKey="vehicle_model" value={data.vehicle_model} onChange={(e) => setField("vehicle_model", e.target.value)} /></Field>
          </div>
        )}

        {/* Documents */}
        {stepKey === "documents" && (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-[#0A192F]">Documents</h2>
              <p className="text-sm text-slate-500 mt-1">Accepted formats: PDF, JPG, PNG. Max 15MB per file.</p>
            </div>
            {DOC_LIST.map((d) => {
              const uploaded = data.documents[d.key];
              const isUploading = uploading[d.key];
              return (
                <div key={d.key} className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-semibold text-[#0A192F]">{d.label} {d.required ? <span className="text-red-500">*</span> : <span className="text-xs text-slate-500">(optional)</span>}</div>
                      {uploaded && <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {uploaded.original_filename}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <label data-testid={T.apply.docUpload(d.key)} className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-[#1D4ED8] text-sm font-semibold text-slate-700 transition-colors">
                        <Upload className="w-4 h-4" /> {isUploading ? "Uploading…" : "Upload"}
                        <input type="file" accept=".pdf,image/png,image/jpeg" className="hidden" onChange={(e) => uploadDoc(d.key, e.target.files?.[0])} />
                      </label>
                      {uploaded && (
                        <button onClick={() => removeDoc(d.key)} className="p-2 rounded-full bg-white border border-slate-200 hover:border-red-300 hover:text-red-600" aria-label="Remove">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="rounded-2xl bg-blue-50 border border-blue-100 p-5">
              <h3 className="font-semibold text-[#0A192F]">Driver Information</h3>
              <p className="mt-2 text-xs text-slate-500">Scheduling flexibility, driver support, professional resources, and orientation may be available and will be discussed during the interview process.</p>
            </div>

            <div className="rounded-2xl border border-slate-200 p-5">
              <h3 className="font-semibold text-[#0A192F]">Driver Eligibility</h3>
              <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {ELIGIBILITY.map((e) => (
                  <li key={e} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {e}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Agreements */}
        {stepKey === "agreements" && (
          <div className="space-y-5">
            <h2 className="text-xl font-semibold text-[#0A192F]">Agreements & Authorizations</h2>
            <div className="rounded-2xl border border-slate-200 p-5 bg-slate-50 max-h-64 overflow-y-auto text-sm text-slate-700 leading-relaxed">
              <div className="font-semibold text-[#0A192F] mb-2">Release, Waiver of Liability and Hold Harmless Agreement</div>
              <p>By submitting this application, the applicant acknowledges that the information provided is truthful and complete to the best of their knowledge, and consents to review of the information submitted for the purposes of employment evaluation.</p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid={T.apply.waiverCheckbox} type="checkbox" checked={data.waiver_accepted} onChange={(e) => setField("waiver_accepted", e.target.checked)} className="mt-1 w-5 h-5 rounded border-slate-300 text-[#1D4ED8] focus:ring-blue-500" />
              <span className="text-sm text-slate-700">I have read and agree to the <strong>Release, Waiver of Liability and Hold Harmless Agreement</strong>.</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={data.authorizations_accepted} onChange={(e) => setField("authorizations_accepted", e.target.checked)} className="mt-1 w-5 h-5 rounded border-slate-300 text-[#1D4ED8] focus:ring-blue-500" />
              <span className="text-sm text-slate-700">I authorize Moreno Quality Transportation to review the information submitted in this application.</span>
            </label>
          </div>
        )}

        {/* Signature */}
        {stepKey === "signature" && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-[#0A192F]">Digital Signature</h2>
            <p className="text-sm text-slate-600">Please sign below using your mouse or touchscreen.</p>
            <SignaturePad value={data.signature_data} onChange={(v) => setField("signature_data", v)} />
          </div>
        )}

        {/* Review */}
        {stepKey === "review" && (
          <div className="space-y-5">
            <h2 className="text-xl font-semibold text-[#0A192F]">Review & Submit</h2>
            <ReviewBlock title="Personal Information" items={[
              ["Name", `${data.first_name} ${data.last_name}`],
              ["Phone", data.phone], ["Email", data.email],
              ["City / State / ZIP", `${data.city}, ${data.state} ${data.zip}`],
              ["Date of Birth", data.dob],
            ]} onEdit={() => setStep(0)} />
            <ReviewBlock title="Driver Information" items={[
              ["License #", data.license_number], ["License State", data.license_state],
              ["License Expiration", data.license_expiration], ["Years Experience", data.years_experience],
              ["Driving History", data.driving_history || "—"],
            ]} onEdit={() => setStep(1)} />
            <ReviewBlock title="Vehicle Information" items={[
              ["Vehicle", `${data.vehicle_year} ${data.vehicle_model}`.trim()],
            ]} onEdit={() => setStep(2)} />
            <ReviewBlock title="Uploaded Documents" onEdit={() => setStep(3)} items={
              DOC_LIST.map((d) => [d.label, data.documents[d.key]?.original_filename || (d.required ? "Missing" : "Not provided")])
            } />
            <ReviewBlock title="Agreements" onEdit={() => setStep(4)} items={[
              ["Waiver Accepted", data.waiver_accepted ? "Yes" : "No"],
              ["Authorizations", data.authorizations_accepted ? "Accepted" : "—"],
            ]} />
            <div className="rounded-2xl border border-slate-200 p-5 bg-white">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-[#0A192F]">Signature</div>
                <button onClick={() => setStep(5)} className="text-xs font-semibold text-[#1D4ED8]">Edit</button>
              </div>
              {data.signature_data ? <img src={data.signature_data} alt="Signature" className="mt-3 border border-slate-200 rounded-lg max-h-32" /> : <div className="text-sm text-red-500 mt-2">Signature missing</div>}
            </div>
          </div>
        )}

        {/* Nav */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <button onClick={prev} disabled={step === 0} data-testid={T.apply.prev} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 disabled:opacity-40 text-slate-700 hover:bg-slate-50 font-semibold">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} data-testid={T.apply.next} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#1D4ED8] hover:bg-blue-800 text-white font-semibold">
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid={T.apply.submit} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold">
              {submitting ? "Submitting…" : "Submit Driver Application"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({ title, items, onEdit }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-white">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-[#0A192F]">{title}</div>
        <button onClick={onEdit} className="text-xs font-semibold text-[#1D4ED8]">Edit Application</button>
      </div>
      <dl className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {items.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1.5">
            <dt className="text-slate-500">{k}</dt>
            <dd className="text-slate-800 font-medium text-right break-all">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
